import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseComponentProps } from '../../types';
import { LogoIcon, ShareIcon, ArrowRightIcon } from './HeaderIcons';
import { useHeaderMenuItems, useHeaderShare, headerStyles } from './HeaderUtil';
import { LanguageToggle, useHeaderTranslation } from '../../modules/i18n';
import { useKeycloakAuth } from '../../contexts/KeycloakAuthProvider';
import { jwtDecode } from 'jwt-decode';

interface HeaderDesktopProps extends BaseComponentProps {
  title?: string;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  isSettingsOpen: boolean;
  setIsSettingsOpen: (open: boolean) => void;
  hoveredSubmenu: string | null;
  setHoveredSubmenu: (submenu: string | null) => void;
  settingsRef: React.RefObject<HTMLDivElement | null>;
  showUserSettings?: boolean;
  setShowUserSettings?: (show: boolean) => void;
}

const HeaderDesktop: React.FC<HeaderDesktopProps> = ({
  title = 'ChatbotFinbot',
  className = '',
  isDarkMode,
  toggleDarkMode,
  isSettingsOpen,
  setIsSettingsOpen,
  hoveredSubmenu,
  setHoveredSubmenu,
  settingsRef
}) => {
  const navigate = useNavigate();
  const { t } = useHeaderTranslation();
  const { desktopMenuItems } = useHeaderMenuItems(isDarkMode, toggleDarkMode, setIsSettingsOpen);
  const { handleShare } = useHeaderShare(title);
  const { getAccessToken } = useKeycloakAuth();

  // Get user initial from token
  const getUserInitial = () => {
    try {
      const token = getAccessToken();
      if (token) {
        const decoded: any = jwtDecode(token);
        const name = decoded.name || decoded.preferred_username || decoded.given_name || decoded.email || 'U';
        return name.charAt(0).toUpperCase();
      }
    } catch (error) {
      console.error('Failed to decode token:', error);
    }
    return 'U';
  };

  const userInitial = getUserInitial();

  return (
    <header className={`${headerStyles.container} ${className}`}>
      <div className='w-full px-4'>
        <div className='flex h-16 items-center justify-between'>
          {/* Logo và title */}
          <div className='flex items-center space-x-3'>
            <button onClick={() => navigate('/')} className={headerStyles.logoButton} title={t('navigation.home')}>
              <LogoIcon />
            </button>
          </div>

          {/* Desktop actions */}
          <div className='flex items-center space-x-3'>
            {/* Language Toggle */}
            <LanguageToggle variant="button" size="sm" showText={true} className="h-8" />
            {/* Share button với text - tối ưu để tránh "nháy" */}
            <button
              onClick={handleShare}
              className='inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 dark:border-border-dark dark:bg-dark-background-secondary dark:text-dark-text dark:hover:bg-dark-background-hover'
              title={t('actions.share')}
            >
              <span className='mr-2'>
                <ShareIcon />
              </span>
              {t('actions.share')}
            </button>

            {/* Avatar with dropdown */}
            <div className='relative' ref={settingsRef}>
              <button onClick={() => setIsSettingsOpen(!isSettingsOpen)} className={`${headerStyles.avatarButton} h-8 w-8`}>
                <span className='text-sm font-medium text-white'>{userInitial}</span>
              </button>

              {/* Dropdown menu */}
              {isSettingsOpen && (
                <div className={headerStyles.dropdown}>
                  {desktopMenuItems.map((item, index) => (
                    <div
                      key={index}
                      className='relative'
                      onMouseEnter={() => item.hasSubmenu && setHoveredSubmenu(item.id)}
                      onMouseLeave={() => item.hasSubmenu && setHoveredSubmenu(null)}
                    >
                      <button
                        onClick={() => {
                          if (!item.hasSubmenu) {
                            item.action();
                            setIsSettingsOpen(false);
                          }
                        }}
                        className={`${headerStyles.menuItem} justify-between ${
                          item.id === 'logout' ? headerStyles.menuItemLogout : headerStyles.menuItemDefault
                        }`}
                      >
                        <div className='flex items-center space-x-3'>
                          <span className={`${item.id === 'logout' ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-dark-text-secondary'}`}>
                            {item.icon}
                          </span>
                          <span>{item.label}</span>
                        </div>
                        {item.hasSubmenu && <ArrowRightIcon />}
                      </button>

                      {/* Submenu cho desktop */}
                      {item.hasSubmenu && hoveredSubmenu === item.id && item.submenuItems && (
                        <div className={headerStyles.submenu}>
                          {item.submenuItems.map((subItem, subIndex) => (
                            <button
                              key={subIndex}
                              onClick={() => {
                                subItem.action();
                                setIsSettingsOpen(false);
                                setHoveredSubmenu(null);
                              }}
                              className={`${headerStyles.menuItem} space-x-3 ${
                                subItem.isActive ? headerStyles.menuItemActive : headerStyles.menuItemDefault
                              }`}
                            >
                              <span className={`${subItem.isActive ? 'text-primary dark:text-white' : 'text-gray-500 dark:text-dark-text-secondary'}`}>
                                {subItem.icon}
                              </span>
                              <span>{subItem.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderDesktop;
